import { travelDay } from '../../lib/calendar'
import { anzahl, hours, money, num, zeile } from '../../lib/format'
import { fahrzeug, FUEL_UNIT } from '../../lib/vehicle'
import type { DiaryEntry, Stage, Trip } from '../../lib/types'
import { ATTRIBUTION } from '../map/basemap'
import { RouteMap } from '../map/RouteMap'
import { periodLabel, routeLabel, type PrintMetrics } from './metrics'
import { NotizFeld, PageFrame } from './PageFrame'
import { uebersichtsListe } from './sections'

/**
 * Die drei Seiten vor den Etappen: Titel, Übersichtskarte, Zahlen.
 *
 * Sie beantworten in dieser Reihenfolge drei Fragen - was ist das hier, wo
 * führt es hin, wie gross ist es. Erst danach fängt die Reise an.
 */

interface TitleProps {
  trip: Trip
  metrics: PrintMetrics
  /** Adresse des Titelbilds, wenn eines taugt. Sonst bleibt die Seite Schrift. */
  photoUrl?: string
}

/**
 * Die Titelseite.
 *
 * Mit Bild wird es ein randloses Foto mit einem Band aus Asphaltfarbe darunter,
 * ohne Bild eine typografische Seite. Das ist kein Notbehelf: eine Seite aus
 * Schrift, einem Rahmen und dem Kennzeichenschild sieht besser aus als ein
 * kleines Foto, das auf A5 aufgeblasen wurde.
 *
 * Das Band ist eine Hintergrundfarbe und braucht damit den Haken
 * "Hintergrundgrafiken" im Druckdialog. Ohne ihn steht die Schrift trotzdem da,
 * nur eben dunkel auf hell statt hell auf dunkel - deshalb bekommt das Band eine
 * Umrandung, die immer druckt, und die Schrift eine Farbe, die auf beidem
 * lesbar bleibt.
 */
export function TitlePage({ trip, metrics, photoUrl }: TitleProps) {
  const stages = trip.stages.filter((stage) => !stage.skip)
  const route = routeLabel(stages)
  const zeitraum = periodLabel(metrics.period)

  return (
    <PageFrame kind={photoUrl ? 'titel' : 'titel dr-seite--titel-schrift'}>
      {photoUrl && (
        <img className="dr-titel-bild" src={photoUrl} alt="" loading="eager" decoding="sync" />
      )}

      {/*
        Ohne Bild steht die Marke oben und der Rest unten: eine Seite, die nur
        unten beschriftet ist, sieht nach vergessenem Kopf aus, eine, die an
        beiden Enden gehalten wird, nach Absicht. Mit Bild geht das nicht - eine
        helle Zeile über einem unbekannten Foto ist nicht lesbar, dort gehört
        sie ins dunkle Band.
      */}
      {!photoUrl && <span className="dr-titel-marke dr-titel-oben">Roadbook</span>}

      <div className="dr-titel-band">
        {photoUrl && <span className="dr-titel-marke">Roadbook</span>}
        {/*
          Der Titel darf nicht fehlen - eine Umschlagseite ohne Überschrift
          sieht nach einem Druckfehler aus. Trägt die Reise noch keinen, steht
          dort "Roadbook", und die Zeile darunter lässt weg, was leer ist.
        */}
        <h1 className="dr-titel-name">{trip.meta.title || 'Roadbook'}</h1>
        {route && <p className="dr-titel-route">{route}</p>}
        <p className="dr-titel-zeile">
          {zeile([
            fahrzeug(trip).name,
            anzahl(trip.meta.crew, 'Person', 'Personen'),
            anzahl(metrics.stageCount, 'Etappe', 'Etappen'),
          ])}
        </p>
        {zeitraum && <p className="dr-titel-zeile">{zeitraum}</p>}
      </div>
    </PageFrame>
  )
}

interface OverviewProps {
  stages: Stage[]
  diary: Record<string, DiaryEntry>
  page: number
  fuss: string
  mapUrl?: string
}

/**
 * Die Übersichtskarte mit der Etappenliste darunter.
 *
 * Fällt der Bildexport aus, tritt die schematische Karte an - und zwar hier
 * ausdrücklich die grosse aus features/map: für die ganze Route ist sie
 * gebaut, mit einem Gradnetz aus deren Umriss und den Namen von Anfang und
 * Ende, und genau dafür taugt sie.
 */
export function OverviewPage({ stages, diary, page, fuss, mapUrl }: OverviewProps) {
  // Was das Raster nicht trägt, kommt gar nicht erst ins Markup - und die
  // Zahl, die dabei abfällt, steht unten auf der Seite.
  const { gezeigt, rest } = uebersichtsListe(stages)

  return (
    <PageFrame kind="uebersicht" page={page} fuss={fuss}>
      <h2 className="dr-titelzeile">Die Route</h2>

      <figure className="dr-karte dr-karte--gross">
        {mapUrl ? (
          <img src={mapUrl} alt={`Karte mit ${String(stages.length)} Etappen`} loading="eager" />
        ) : (
          <RouteMap stages={stages} diary={diary} selectedId="" onSelect={() => undefined} />
        )}
        <figcaption>{mapUrl ? ATTRIBUTION : 'Schematische Karte, ohne Kartendaten'}</figcaption>
      </figure>

      {/*
        Die Liste steht unter der Karte und nimmt sich, was übrig ist: zwei
        Spalten zu acht Zeilen, also UEBERSICHT_PLAETZE Etappen. Was darüber
        hinausgeht, bekommt eine Zeile mit der Zahl statt einer stillen Lücke -
        siehe unten.
      */}
      <ol className="dr-etappenliste">
        {gezeigt.map((stage) => (
          <li key={stage.id}>
            {/*
              Der Reisetag, gerechnet über alle Etappen und nicht über die
              gekürzte Liste: die Zahl hier muss dieselbe sein, die im Kopf der
              Etappenseite steht, sonst schickt die Übersicht auf die falsche
              Seite. Ausgelassene Etappen stehen hier ohnehin nicht, also gibt
              es hier auch keine Zeile ohne Tag.
            */}
            <span className="dr-tagnummer">{travelDay(stages, stage)}</span>
            <span className="dr-etappenziel">{stage.to}</span>
            <span className="dr-etappenkm">{num(stage.km)} km</span>
          </li>
        ))}
      </ol>

      {/*
        Der Hinweis, den diese Seite bisher als einzige nicht hatte.

        Die Etappenseiten sagen überall, wenn eine Liste gekürzt ist ("und N
        weitere"); hier stand die Kürzung nur als Kommentar im Quelltext. Das
        ist ausgerechnet die Seite, auf der man nachsieht, was noch kommt: wird
        unterwegs eine Etappe eingeschoben, weil eine Strasse gesperrt ist,
        fiele sie sonst spurlos von der Übersicht.
      */}
      {rest > 0 && (
        <p className="dr-listenrest">
          und {rest} weitere {rest === 1 ? 'Etappe' : 'Etappen'} — auf den folgenden Seiten
        </p>
      )}
    </PageFrame>
  )
}

interface MetricsProps {
  trip: Trip
  metrics: PrintMetrics
  page: number
  fuss: string
}

/** Die Reise in Zahlen. */
export function MetricsPage({ trip, metrics, page, fuss }: MetricsProps) {
  const waehrung = trip.meta.currency
  // Verbrauch und Einheit kommen vom Fahrzeug: „l/100 km“ stand hier fest, und
  // ein Elektroauto verbraucht keine Liter.
  const auto = fahrzeug(trip)

  const kacheln: { wert: string; text: string }[] = [
    { wert: String(metrics.stageCount), text: 'Etappen' },
    { wert: num(metrics.plannedKm), text: 'Kilometer geplant' },
    { wert: num(metrics.drivenKm), text: 'Kilometer gefahren' },
    { wert: hours(metrics.hours), text: 'Stunden am Steuer' },
    { wert: String(metrics.countries.length), text: 'Länder' },
    { wert: String(metrics.drivenCount), text: 'Tage gefahren' },
    { wert: money(metrics.spent, waehrung), text: 'ausgegeben' },
    { wert: money(metrics.fuelCost, waehrung), text: 'Kraftstoff geschätzt' },
    { wert: String(metrics.photoCount), text: 'Fotos im Buch' },
  ]

  return (
    <PageFrame kind="kennzahlen" page={page} fuss={fuss}>
      <h2 className="dr-titelzeile">Die Reise in Zahlen</h2>

      <div className="dr-kacheln">
        {kacheln.map((kachel) => (
          <div key={kachel.text} className="dr-kachel">
            <b>{kachel.wert}</b>
            <span>{kachel.text}</span>
          </div>
        ))}
      </div>

      {metrics.countries.length > 0 && (
        <p className="dr-laenderzeile">{metrics.countries.join(' · ')}</p>
      )}

      <dl className="dr-fussnoten">
        {metrics.period && (
          <div>
            <dt>Zeitraum</dt>
            <dd>{periodLabel(metrics.period)}</dd>
          </div>
        )}
        {/*
          Nur wo etwas eingetragen ist. Ohne die Bedingung stand auf dem Papier
          ", 0 l/100 km angenommen" unter der Überschrift "Fahrzeug" - eine
          gedruckte Behauptung, das Fahrzeug verbrauche nichts.
        */}
        {(auto.name || auto.consumption > 0) && (
          <div>
            <dt>Fahrzeug</dt>
            <dd>
              {[
                auto.name,
                auto.consumption > 0 &&
                  `${String(auto.consumption)} ${FUEL_UNIT[auto.fuel]}/100 km angenommen`,
              ]
                .filter(Boolean)
                .join(', ')}
            </dd>
          </div>
        )}
        {metrics.routedKm > 0 && (
          <div>
            {/*
              Die berechnete Strecke steht neben der geplanten und nicht an ihrer
              Stelle: die Handwerte sind die Grundlage der Reise, der Dienst
              rechnet nur nach. Stimmen beide nicht zusammen, sieht man es genau
              hier.
            */}
            <dt>Strecke berechnet</dt>
            <dd>{num(metrics.routedKm)} km auf den Etappen mit aktueller Route</dd>
          </div>
        )}
      </dl>

      <NotizFeld titel="Tanken, Wechselkurse, Tachostände" />
    </PageFrame>
  )
}
